// transform.js
import { List, Map, fromJS } from 'immutable';

/**
 * Default card definitions for the dashboard.
 * These provide component type, i18n title keys, themes, base queries, and positions.
 * NOTE: The "terminated" card's default query includes currentMonth=true,
 *       which is not the same as "within the last rolling month" used in stats below.
 */
const CARDS = [
  {
    component: 'DlpkInfoCard',
    type: 'quote',
    config: {
      title: 'dashboardCard.title.pending',
      theme: 'orange',
      data: [{ query: { status: ['quote'] }, route: '/contracts', value: 0 }],
    },
    position: 1,
  },
  {
    component: 'DlpkInfoCard',
    type: 'active',
    config: {
      title: 'dashboardCard.title.active',
      theme: 'green',
      data: [{ query: { status: ['active'] }, route: '/contracts', value: 0 }],
    },
    position: 2,
  },
  {
    component: 'DlpkInfoCard',
    type: 'terminated',
    config: {
      title: 'dashboardCard.title.terminated',
      theme: 'orange',
      data: [{ query: { status: ['terminated'], currentMonth: ['true'] }, route: '/contracts', value: 0 }],
    },
    position: 3,
  },
  {
    component: 'DlpkInfoCard',
    type: 'unpaid',
    config: {
      title: 'dashboardCard.title.warning',
      theme: 'red',
      data: [{ query: { status: ['active', 'declined', 'terminated', 'resiliated'], isOutstanding: ['true'] }, route: '/contracts', value: 0 }],
    },
    position: 4,
  },
];

/**
 * Returns true if the given date string is within the last rolling month
 * [today at 00:00 local time down to the same clock time one month earlier].
 *
 * @param {string|Date|null|undefined} dateStr - Date value parseable by `new Date()`.
 * @returns {boolean}
 */
const isWithinLastMonth = (dateStr) => {
  if (!dateStr) return false;
  const toMidnight = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const target = toMidnight(dateStr);
  if (isNaN(target.getTime())) return false;
  const today = toMidnight(new Date());
  const oneMonthAgo = new Date(today); oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return target >= oneMonthAgo && target <= today;
};

/**
 * Build the dashboard widgets list from contracts and existing widgets.
 *
 * Algorithm:
 *  1) Compute stats per status. For declined/resiliated/terminated, count only if lastTermEndDate is within the last month.
 *  2) Index existing widgets by their "type" (Immutable Maps).
 *  3) For each default card type:
 *     - "unpaid": use existing as-is if present, else the default.
 *     - others: merge defaults + existing (existing wins), then force config.data[0].value = computed stat.
 *  4) Append any extra existing widgets whose type is not declared in CARDS.
 *  5) Return the final Immutable List.
 *
 * @param {Array<Object>} [contracts=[]] - Plain JS array of contract objects with fields:
 *   - contractStatus: 'active'|'quote'|'terminated'|'declined'|'resiliated'|...
 *   - lastTermEndDate: ISO string or Date (used for rolling-month filtering of some statuses)
 * @param {import('immutable').List} [widgets=List()] - Immutable List of Immutable Maps (existing widgets).
 * @returns {import('immutable').List} An Immutable List of widget Maps in the target order with computed values.
 */
export const toDashboardCards = (contracts = [], widgets = List()) => {
  // 1) Compute stats (except "unpaid")
  const stats = { active: 0, quote: 0, terminated: 0, declined: 0, resiliated: 0 };

  for (const c of contracts) {
    switch (c?.contractStatus) {
      case 'active': stats.active++; break;
      case 'quote': stats.quote++; break;
      case 'declined': if (isWithinLastMonth(c?.lastTermEndDate)) stats.declined++; break;
      case 'resiliated': if (isWithinLastMonth(c?.lastTermEndDate)) stats.resiliated++; break;
      case 'terminated': if (isWithinLastMonth(c?.lastTermEndDate)) stats.terminated++; break;
      default: break;
    }
  }

  // 2) Index existing widgets by type
  const existingByType = Object.create(null);
  widgets.forEach((w) => {
    const t = w.get('type');
    if (t) existingByType[t] = w;
  });

  // 3) Build the core cards in CARDS order
  const builtFromCards = CARDS.map((card) => {
    const type = card.type;
    const defaults = fromJS(card);
    const existing = existingByType[type];

    if (type === 'unpaid') {
      // Keep unpaid as-is if present; otherwise use defaults
      return existing ? existing : defaults;
    }

    // Merge defaults + existing (existing wins), then safely set config.data[0].value
    let merged = existing ? defaults.mergeDeep(existing) : defaults;
    const value = stats[type] ?? 0;

    // Safer write into config.data[0].value:
    merged = merged.updateIn(['config', 'data'], (lst) => {
      const ensureList = List.isList(lst) ? lst : List();
      const first = ensureList.get(0);
      const firstUpdated = Map.isMap(first)
        ? first.set('value', value)
        : fromJS({ query: {}, route: '/contracts', value });
      return ensureList.size === 0 ? List([firstUpdated]) : ensureList.set(0, firstUpdated);
    });

    return merged;
  });

  // 4) Keep extra widgets not listed in CARDS
  const typesInCards = new Set(CARDS.map(c => c.type));
  const extras = widgets.filter(w => !typesInCards.has(w.get('type')));

  // 5) Return final Immutable List
  return List(builtFromCards).concat(extras);
};